import { useCallback, useMemo } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useQueryClient } from 'react-query';

import { QueryEntity } from './types';

export enum FetchMethod {
  GET = 'GET',
  PATCH = 'PATCH',
  POST = 'POST',
  DELETE = 'DELETE',
  PUT = 'PUT',
}

interface UseGetFetchConfig {
  method: FetchMethod;
  data?: Record<string, any>;
  isFormData?: boolean;
}

export interface FetchConfig {
  method: FetchMethod;
  mode: RequestMode;
  cache: RequestCache;
  credentials: RequestCredentials;
  headers: Headers;
}

export const useFetcher = () => {
  const authInfo = useAuthInfo();
  const queryClient = useQueryClient();

  const fetcher = async <T = unknown>(url: string): Promise<T> => {
    const accessToken = await authInfo.tokens.getAccessToken();

    const res = await fetch(url, {
      method: FetchMethod.GET,
      mode: 'cors' as RequestMode,
      cache: 'no-cache' as RequestCache,
      credentials: 'same-origin' as RequestCredentials,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }),
    });

    if (res.status === 403) {
      // If the terms-of-use gate was enabled mid-session, refetch the user so
      // TermsOfUseGate redirects to the accept-terms page.
      const body = await res
        .clone()
        .json()
        .catch(() => null);

      if (body?.errorCode === 'TERMS_OF_USE_NOT_ACCEPTED') {
        queryClient.invalidateQueries([QueryEntity.User]);
      }
    }

    if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);

    const json = await res.json();
    return json;
  };

  return fetcher;
};

const useGetFetchConfig = () => {
  // TODO: Deprecate
  const authInfo = useAuthInfo();
  const accessToken = authInfo.accessToken;

  const fetchConfigGET: FetchConfig = useMemo(
    () => ({
      method: FetchMethod.GET,
      mode: 'cors' as RequestMode,
      cache: 'no-cache' as RequestCache,
      credentials: 'same-origin' as RequestCredentials,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }),
    }),
    [accessToken],
  );

  const getFetchConfig = useCallback(
    ({ method, data, isFormData }: UseGetFetchConfig) => {
      const body = isFormData ? (data as FormData) : JSON.stringify(data);

      const headers: HeadersInit = isFormData
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          };

      return {
        method,
        cache: 'no-cache' as RequestCache,
        credentials: 'same-origin' as RequestCredentials,
        headers: new Headers(headers),
        body: data ? body : undefined,
      };
    },
    [accessToken],
  );

  return {
    fetchConfigGET,
    getFetchConfig,
  };
};

export default useGetFetchConfig;
